import React from 'react';

import * as ROUTES from '@constants/routes';

import type { Profile } from '@models/session';

import {
  useAdminListRoles,
  useAdminAddRoleToUser,
  useAdminRemoveRoleFromUser,
} from 'generated/admin.generated';

import { Editor } from '../Common';

const messages: Editor.EditorMessages = {
  add: { id: 'admin.users.roles.add' },
  remove: { id: 'admin.users.roles.remove' },
  confirmAdd: {
    id: 'admin.users.roles.add.confirm',
  },
  confirmRemove: {
    id: 'admin.users.roles.remove.confirm',
  },
  title: { id: 'admin.users.roles' },
};

interface UserRoleEditorProps {
  user: Profile | null;
  loading: boolean;
}

const UserRoleEditor = ({
  user,
  loading: baseLoading,
}: UserRoleEditorProps) => {
  const { data: rolesData, loading: rolesLoading } = useAdminListRoles();
  const [addRole] = useAdminAddRoleToUser();
  const [removeRole] = useAdminRemoveRoleFromUser();

  const handleAddRole = async (option: Editor.EditorOption) => {
    if (!user) {
      return;
    }
    await addRole({
      variables: {
        input: {
          role_id: option.id,
          user_id: user.user_id,
        },
      },
    });
  };

  const handleRemoveRole = async (option?: Editor.EditorOption) => {
    if (!option || !user) {
      return;
    }
    await removeRole({
      variables: {
        input: {
          role_id: option.id,
          user_id: user.user_id,
        },
      },
    });
  };

  const loading = baseLoading || rolesLoading;

  const options = React.useMemo(() => {
    return rolesData?.listRoles.map<Editor.EditorOption>((role) => ({
      id: role.role_id,
      name: role.role_name,
      url: `${ROUTES.ADMIN_MANAGE_ROLES}${role.role_id}`,
    }));
  }, [rolesData]);

  const selected = React.useMemo(() => {
    return user?.roles?.map<Editor.EditorOption>((role) => ({
      id: role.role_id,
      name: role.role_name,
      url: `${ROUTES.ADMIN_MANAGE_ROLES}${role.role_id}`,
    }));
  }, [user]);

  return (
    <Editor.default
      selected={selected}
      options={options}
      messages={messages}
      onRemove={handleRemoveRole}
      onAdd={handleAddRole}
      loading={loading}
    />
  );
};

export default UserRoleEditor;
